import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { getQuoteApi, createQuoteApi, updateQuoteApi, deleteQuoteFileApi } from '../api/quoteApi';
import api from '../api/axios';
import SearchableSelectWithAdd from '../components/SearchableSelectWithAdd';
import ColumnFilterInput from '../components/ColumnFilterInput';
import { normalizeSearch, matchesItemSpaceMaterialLine } from '../utils/textSearch';
import {
  activeQuoteItems,
  isQuoteAddonLine,
  isQuoteLineEnabled,
  quoteLineBase,
  quoteLineDiscountAmount,
  quoteLineNetBeforeGst,
  quoteChargesAdjustment,
} from '../utils/quoteLineMath';
import { formatInr } from '../utils/formatInr';
import { cleanMasterDescription } from '../utils/cleanMasterDescription';
import { cleanQuoteItemForSave, isMeaningfulQuoteItem, normalizeQuoteItem } from '../utils/normalizeQuoteItems';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:5000';

const QUOTE_TITLE_OPTIONS = ['Initial quotation', 'Actual quotation'];

const UOM_PRESET_OPTS = ['sqft', 'rft', 'unit', 'mm', 'cm', 'inch', 'ft', 'sqm', 'kg', 'litre', 'nos'];

const UOM_ALIASES = { rmt: 'rft', rm: 'rft', meter: 'unit', mtr: 'unit', metres: 'unit', pcs: 'nos', numbers: 'nos', 'no.s': 'nos' };

/** UOM on the line and/or populated `item`; match `knownUoms` case-insensitively (defaults to presets only). */
function resolveQuoteLineUom(line, knownUoms) {
  const list = knownUoms && knownUoms.length ? knownUoms : UOM_PRESET_OPTS;
  const direct = line?.uom != null && String(line.uom).trim() !== '' ? String(line.uom).trim() : '';
  const pop =
    typeof line?.item === 'object' && line.item != null && line.item.uom != null
      ? String(line.item.uom).trim()
      : '';
  const raw = direct || pop;
  if (!raw) return 'sqft';
  const lower = raw.toLowerCase();
  const hit = list.find((u) => u.toLowerCase() === lower);
  if (hit) return hit;
  const mapped = UOM_ALIASES[lower];
  if (mapped) {
    const h2 = list.find((u) => u.toLowerCase() === mapped.toLowerCase());
    if (h2) return h2;
    const p = UOM_PRESET_OPTS.find((u) => u.toLowerCase() === mapped.toLowerCase());
    if (p) return p;
  }
  return raw;
}

const Space_OPTS = ['Entrance','Living Room','Kitchen','Master Bedroom 1',"Children's Bedroom",'Guest Bedroom','Parents Room','Mandir Room','Electrical Work','Bathroom','Balcony','Other'];
/** Quote line “Material” picker — not Material Master; extras saved via /material-options */
const MATERIAL_PRESET_OPTS = [
  'Plywood and Laminate',
  'Gypsum',
  'Plywood and Glass',
  'Approx. Points',
  'Aluminium',
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const getTierMultiplier = (tier) => {
  if (tier === 'essential') return 0.90;
  if (tier === 'prestige') return 1.20;
  return 1.0;
};

const calcTierRate = (baseRate, tier) => {
  const mult = getTierMultiplier(tier);
  return Math.round((Number(baseRate) || 0) * mult);
};

const calcAmount = (item) => {
  const len = Number(item.length) || 0;
  const ht = Number(item.height) || 0;
  const qty = (len > 0 && ht > 0) ? (len * ht) : (Number(item.qty) > 0 ? Number(item.qty) : 1);
  let amount = qty * (Number(item.rate) || 0);

  // discount
  if (normalizeDiscountType(item.discountType) === "%") {
    amount -= (amount * (Number(item.discount) || 0)) / 100;
  } else {
    amount -= (Number(item.discount) || 0);
  }

  return Math.max(0, Math.round(amount * 100) / 100);
};

const isElectricalSection = (section) =>
  /^electrical work$/i.test(String(section || '').trim());

const defaultDiscountForSection = () => 0;
const markAddonState = (item, enabled = true) => {
  if (!isQuoteAddonLine(item)) return item;
  return {
    ...item,
    _isAddon: true,
    _addonGroup: item?._addonGroup || item?.space || item?.section || 'Electrical Work',
    _addonEnabled: enabled,
  };
};
const CURRENCY_DISCOUNT_TYPE = '\u20b9';
const normalizeDiscountType = (discountType) => {
  const value = String(discountType || '').trim();
  return value === CURRENCY_DISCOUNT_TYPE || value === 'Rs' || value === 'INR' || value === '₹'
    ? CURRENCY_DISCOUNT_TYPE
    : '%';
};
/** baseSubTotal = Σ line base (qty×rate); discountTotal = Σ line discounts; subTotal = net before charges. */
const calcTotals = (items) => {
  let baseSum = 0;
  let discSum = 0;
  let netSum = 0;
  items.forEach((it) => {
    baseSum += quoteLineBase(it);
    discSum += quoteLineDiscountAmount(it);
    netSum += quoteLineNetBeforeGst(it);
  });
  const baseSubTotal = Math.round(baseSum * 100) / 100;
  const discountTotal = Math.round(discSum * 100) / 100;
  const subTotal = Math.round(netSum * 100) / 100;
  return { baseSubTotal, discountTotal, subTotal, total: subTotal };
};

const emptyItem = () => ({
  _tmpId: Math.random().toString(36).slice(2),
  itemName:'', itemDetails:'', space:'', category:'',
  internalFinish:'', externalFinish:'',
  length:0, height:0, qty:1, uom:'sqft',
  rate:0, discountType: "%", discount:0, amount:0,   remarks:'', image:null,
});

/** Paginated load of Item Master (same contract as Add Items modal). */
async function fetchItemMasterCatalog(apiClient, { excludeTemplatePresets = true } = {}) {
  const limit = 150;
  const all = [];
  let page = 1;
  let pages = 1;
  do {
    const r = await apiClient.get('/item-master', {
      params: {
        page,
        limit,
        ...(excludeTemplatePresets ? { excludeTemplatePresets: true } : {}),
      },
    });
    const payload = r.data?.data ?? r.data;
    const list = Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(r.data?.items)
        ? r.data.items
        : [];
    const pag = payload?.pagination;
    all.push(...list);
    pages = pag?.pages != null ? Number(pag.pages) : 1;
    if (list.length < limit) break;
    page += 1;
  } while (page <= pages && page < 80);
  return all;
}

const DRAG_MIME = "application/x-tatsavi-quote-row";

// ── Add Items Modal ────────────────────────────────────────────────────────────
function AddItemsModal({ tier = 'signature', onClose, onAdd }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState([]);  const [flItem, setFlItem] = useState('');
  const [flCategory, setFlCategory] = useState('');
  const [flMaterial, setFlMaterial] = useState('');
  /** When true, matches old behaviour: hide BHK “standard pre-quotation” template rows so the list stays short. */
  const [hideBhkTemplateRows, setHideBhkTemplateRows] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const all = await fetchItemMasterCatalog(api, {
          excludeTemplatePresets: hideBhkTemplateRows,
        });
        if (!cancelled) setItems(all);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setItems([]);
          setLoadError(e.response?.data?.message || e.message || 'Could not load items from Item Master.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hideBhkTemplateRows]);

  const filtered = items.filter((it) => {
    const fi = normalizeSearch(flItem);
    const fc = normalizeSearch(flCategory);
    const fm = normalizeSearch(flMaterial);
    if (fi) {
      const blob = `${it.name ?? ''} ${it.itemCode ?? ''} ${it.description ?? ''}`.toLowerCase();
      if (!blob.includes(fi)) return false;
    }
    if (fc && !String(it.itemCategory ?? '').toLowerCase().includes(fc)) return false;
    if (fm) {
      const mb = `${it.quoteMaterial ?? ''} ${it.itemCategory ?? ''} ${it.externalFinish ?? ''} ${it.internalFinish ?? ''} ${it.rates?.[0]?.makeName ?? ''}`.toLowerCase();
      if (!mb.includes(fm)) return false;
    }
    return true;
  });

  const toggle = (id) => setSelectedOrder(prev =>
  prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
);
  const selectAll = (e) => {
  if (e.target.checked) {
    setSelectedOrder(filtered.map(it => it._id));
  } else {
    setSelectedOrder([]);
  }
};

  const handleSubmit = () => {
    const chosen = selectedOrder
  .map(id => items.find(it => it._id === id))
  .filter(Boolean);    if (chosen.length === 0) {
      window.alert('Select one or more rows (checkbox), then click Add to quote.');
      return;
    }
    onAdd(chosen);
    onClose();
  };

  const thS = { padding:'6px 8px', fontSize:11, fontWeight:600, color:'#6B7280', borderBottom:'1px solid #E5E7EB', background:'#F9FAFB', whiteSpace:'nowrap', textAlign:'left' };
  const tdS = { padding:'8px', borderBottom:'1px solid #F3F4F6', fontSize:12, verticalAlign:'middle' };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16 }}>
      <div style={{ background:'#fff',borderRadius:12,width:'100%',maxWidth:860,maxHeight:'85vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:'1px solid #E5E7EB' }}>
          <div>
            <h3 style={{ margin:0,fontSize:16,fontWeight:700,color:'#1F2937' }}>Select Items</h3>
            <p style={{ margin:'6px 0 0',fontSize:12,color:'#6B7280',maxWidth:520 }}>
              Same catalogue as Item Master. Tick rows, then use <strong>Add to quote</strong>.
            </p>
          </div>
          <button onClick={onClose} style={{ background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#9CA3AF' }}>×</button>
        </div>

        <div style={{ padding:'0 20px 10px',borderBottom:'1px solid #F3F4F6' }}>
          <label style={{ display:'inline-flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:12,color:'#374151' }}>
            <input
              type="checkbox"
              checked={hideBhkTemplateRows}
              onChange={(e) => {
                setSelectedOrder([]);
                setHideBhkTemplateRows(e.target.checked);
              }}
            />
            Hide BHK standard catalogue rows (long seeded lists)
          </label>
        </div>

        <div style={{ flex:1,overflowY:'auto' }}>
          {loading ? (
            <div style={{ textAlign:'center',padding:'3rem',color:'#9CA3AF' }}>Loading from Item Master…</div>
          ) : loadError ? (
            <div style={{ textAlign:'center',padding:'2rem',color:'#B91C1C',fontSize:13 }}>{loadError}</div>
          ) : (
            <table style={{ width:'100%',borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thS, width: 36 }} />
                  <th style={{ ...thS, width: 36 }}>#</th>
                  <th style={thS}>Item</th>
                  <th style={thS}>Category</th>
                  <th style={thS}>Material</th>
                  <th style={thS}>Length</th>
                  <th style={thS}>Height</th>
                  <th style={thS}>Qty</th>
                  <th style={thS}>UOM</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Rate (₹)</th>
                </tr>
                <tr style={{ background:'#FAFAFA' }}>
                  <th style={{ ...thS, paddingTop:4,paddingBottom:8 }} colSpan={2} />
                  <th style={{ ...thS, paddingTop:4,paddingBottom:8,verticalAlign:'top' }}>
                    <ColumnFilterInput value={flItem} onChange={(e)=>setFlItem(e.target.value)} placeholder="Filter item…" aria-label="Filter by item" />
                  </th>
                  <th style={{ ...thS, paddingTop:4,paddingBottom:8,verticalAlign:'top' }}>
                    <ColumnFilterInput value={flCategory} onChange={(e)=>setFlCategory(e.target.value)} placeholder="Filter category…" aria-label="Filter by category" />
                  </th>
                  <th style={{ ...thS, paddingTop:4,paddingBottom:8,verticalAlign:'top' }}>
                    <ColumnFilterInput value={flMaterial} onChange={(e)=>setFlMaterial(e.target.value)} placeholder="Filter material…" aria-label="Filter by material" />
                  </th>
                  <th style={{ ...thS, paddingTop:4 }} colSpan={5} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((it, idx) => {
                  const L = Number(it.length) || 0;
                  const H = Number(it.height) || 0;
                  const len = L > 0 ? L : 0;
                  const ht = H > 0 ? H : 0;
                  const qty = (len > 0 && ht > 0) ? (len * ht) : 1;
                  const stdRate = Number(it.rates?.[0]?.clientRate ?? it.rate ?? 0);
                  const rate = calcTierRate(stdRate, tier);
                  const descLine = cleanMasterDescription(it.description, it.defaultSpace);
                  return (
                    <tr
                      key={it._id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggle(it._id)}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#F9FAFB'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                    >
                      <td style={tdS}>
                        <input type="checkbox" checked={selectedOrder.includes(it._id)}
 onChange={() => toggle(it._id)} onClick={(e) => e.stopPropagation()} />
                      </td>
                      <td style={{ ...tdS, color: '#9CA3AF', fontSize: 11 }}>{idx + 1}</td>
                      <td style={tdS}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>{it.name}</div>
                          {descLine ? (
                            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2, maxWidth: 320 }}>
                              {descLine}
                            </div>
                          ) : null}
                          {it.itemCode ? <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>{it.itemCode}</div> : null}
                        </div>
                      </td>
                      <td style={tdS}>{it.itemCategory || '—'}</td>
                      <td style={tdS}>{it.quoteMaterial || '—'}</td>
                      <td style={tdS}>{it.length || '—'}</td>
                      <td style={tdS}>{it.height || '—'}</td>
                      <td style={tdS}>{Number(qty).toFixed(2)}</td>
                      <td style={tdS}>{it.uom || 'sqft'}</td>
                      <td style={{ ...tdS, textAlign: 'right', fontWeight: 600 }}>{formatInr(rate)}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF' }}>No items found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding:'12px 20px',borderTop:'1px solid #E5E7EB',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
          <span style={{ fontSize:12,color:'#9CA3AF' }}>{selectedOrder.length} selected</span>
          <button type="button" onClick={handleSubmit}
            style={{ padding:'8px 24px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
            Add to quote
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create Item Modal (quick create into ItemMaster) ──────────────────────────
function CreateItemModal({ onClose, onCreated, materialOptions, onAddMaterial, uomOptions, onAddUom }) {
  const [form, setForm] = useState({ name: '', quoteMaterial: '', uom: 'sqft', rate: 0, description: '' });
  const [saving, setSaving] = useState(false);
  const h = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const hRate = (e) => setForm((f) => ({ ...f, rate: parseFloat(e.target.value) || 0 }));
  const iS = { width:'100%',padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit' };
  const lS = { display:'block',fontSize:11,fontWeight:600,color:'#6B7280',marginBottom:4,textTransform:'uppercase',letterSpacing:'0.05em' };

  const submit = async (e) => {
    e.preventDefault();
    if (!(form.quoteMaterial || '').trim()) {
      alert('Please choose or add a material.');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('itemCategory', form.quoteMaterial || '');
      fd.append('gst', '0');
      fd.append('rates', JSON.stringify([{ makeName:'Standard', clientRate: form.rate, purchaseRate: 0 }]));
      const r = await api.post('/item-master', fd, { headers:{ 'Content-Type':'multipart/form-data' } });
      onCreated(r.data.data.item);
      onClose();
    } catch(err) { alert(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1001,padding:16 }}>
      <div style={{ background:'#fff',borderRadius:12,width:'100%',maxWidth:480,boxShadow:'0 20px 60px rgba(0,0,0,0.2)',padding:24 }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16 }}>
          <h3 style={{ margin:0,fontSize:16,fontWeight:700,color:'#1F2937' }}>Create New Item</h3>
          <button onClick={onClose} style={{ background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#9CA3AF' }}>×</button>
        </div>
        <form onSubmit={submit}>
          <div style={{ marginBottom:12 }}><label style={lS}>Item Name *</label><input style={iS} name="name" value={form.name} onChange={h} required /></div>
          <div style={{ marginBottom:12 }}><label style={lS}>Description</label><textarea style={{...iS,minHeight:72,resize:'vertical'}} name="description" value={form.description} onChange={h} /></div>
          <div style={{ marginBottom:12 }} onMouseDown={(e) => e.stopPropagation()}>
            <label style={lS}>Material</label>
            <SearchableSelectWithAdd
              value={form.quoteMaterial}
              onChange={(v) => setForm((f) => ({ ...f, quoteMaterial: v }))}
              options={materialOptions}
              onAddNew={onAddMaterial}
              addPrompt="to materials list"
              placeholder="Search or add material…"
              style={iS}
            />
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16 }}>
            <div onMouseDown={(e) => e.stopPropagation()}>
              <label style={lS}>UOM</label>
              <SearchableSelectWithAdd
                value={resolveQuoteLineUom({ uom: form.uom, item: null }, uomOptions)}
                onChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    uom: resolveQuoteLineUom({ uom: (v && String(v).trim()) || 'sqft', item: null }, uomOptions),
                  }))
                }
                options={uomOptions}
                onAddNew={onAddUom}
                addPrompt="to UOM list"
                placeholder="Search or add UOM…"
                style={{ width: '100%', minWidth: 0 }}
              />
            </div>
            <div><label style={lS}>Rate (₹)</label><input style={iS} type="number" name="rate" value={form.rate} onChange={hRate} min={0} step="0.01" /></div>
          </div>
          <div style={{ display:'flex',gap:10,justifyContent:'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding:'8px 18px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:13,cursor:'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding:'8px 20px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
              {saving ? 'Creating…' : 'Create & Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Space Summary Tab ────────────────────────────────────────────────────────
function SpaceSummary({ items, discountLabel = 'Discount', discount = null, additionalDiscount = 0, additionalDiscountLabel = 'Additional Discount' }) {
  const [rowSearch, setRowSearch] = useState('');
  const Spaces = {};
  items.forEach((it) => {
    const sec = it.space || it.section || it.Space || 'No Space';
    if (!Spaces[sec]) Spaces[sec] = { items: 0, baseTotal: 0, discount: 0, subTotal: 0 };
    const base = quoteLineBase(it);
    const disc = quoteLineDiscountAmount(it);
    const net = quoteLineNetBeforeGst(it);
    Spaces[sec].items += 1;
    Spaces[sec].baseTotal += base;
    Spaces[sec].discount += disc;
    Spaces[sec].subTotal += net;
  });

  const { baseSubTotal, discountTotal } = calcTotals(items);
  const effDiscount = discount !== null && discount !== undefined ? Math.max(0, Number(discount) || 0) : discountTotal;
  const finalTotal = Math.max(0, baseSubTotal - effDiscount - additionalDiscount);
  const thS = { padding:'10px 14px',fontSize:12,fontWeight:700,color:'#fff',background:'#2e1a46',borderBottom:'1px solid #2e1a46',borderRight:'1px solid rgba(255,255,255,0.14)',textAlign:'left' };
  const thFilterS = { ...thS, paddingTop:6, paddingBottom:8, color:'#6B7280', background:'#FAFAFA', borderBottom:'1px solid #E5E7EB', borderRight:'1px solid #EEF2F7' };
  const tdS = { padding:'10px 14px',fontSize:13,color:'#374151',borderBottom:'1px solid #E5E7EB',borderRight:'1px solid #F1F5F9',lineHeight:1.35 };
  const rs = normalizeSearch(rowSearch);
  const spaceRows = Object.entries(Spaces).filter(([sec]) => !rs || sec.toLowerCase().includes(rs));

  return (
    <div className="quote-summary-panel">
      <div className="quote-summary-table-wrap">
      <table className="quote-summary-table" style={{ width:'100%',borderCollapse:'collapse' }}>
        <thead>
          <tr>
            <th style={thS}>Space</th>
            <th style={thS}>Items</th>
            <th style={thS}>Base Total</th>
            <th style={thS}>Discount</th>
            <th style={{...thS,textAlign:'right'}}>Sub Total</th>
          </tr>
          <tr style={{ background:'#FAFAFA' }}>
            <th style={{ ...thFilterS, verticalAlign:'top' }}>
              <ColumnFilterInput value={rowSearch} onChange={(e) => setRowSearch(e.target.value)} placeholder="Filter space…" aria-label="Filter by space name" />
            </th>
            <th style={thFilterS} colSpan={4} />
          </tr>
        </thead>
        <tbody>
          {spaceRows.length === 0 ? (
            <tr><td colSpan={5} style={{ ...tdS, textAlign: 'center', color: '#9CA3AF' }}>No spaces match this filter</td></tr>
          ) : spaceRows.map(([sec,d]) => (
            <tr key={sec}>
              <td style={tdS}>{sec}</td>
              <td style={tdS}>{d.items}</td>
              <td style={tdS}>₹{formatInr(d.baseTotal)}</td>
              <td style={tdS}>₹{formatInr(d.discount)}</td>
              <td style={{...tdS,textAlign:'right'}}>₹{formatInr(d.subTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="quote-summary-totals" style={{ textAlign:'right',padding:'14px 16px',borderTop:'1px solid #E5E7EB',fontSize:13 }}>
        <div style={{ color:'#6B7280',marginBottom:3 }}>Sub Total: <strong>₹{formatInr(baseSubTotal)}</strong></div>
        <div style={{ color:'#6B7280',marginBottom:3 }}>{String(discountLabel || '').trim() || 'Discount'}: <strong>₹{formatInr(effDiscount)}</strong></div>
        {additionalDiscount > 0 ? <div style={{ color:'#6B7280',marginBottom:3 }}>{String(additionalDiscountLabel || '').trim() || 'Additional Discount'}: <strong>₹{formatInr(additionalDiscount)}</strong></div> : null}
        <div style={{ color:'#1F2937',fontWeight:700,fontSize:14 }}>Total: ₹{formatInr(finalTotal)}</div>
      </div>
    </div>
  );
}

// ── Main Quote Editor ─────────────────────────────────────────────────────────
export default function QuoteEditor() {
  const { quoteId } = useParams();
  const navigate    = useNavigate();
  const location      = useLocation();
  const [searchParams] = useSearchParams();
  const [clientId, setClientId]   = useState(() => searchParams.get('client') || '');
  const [projectId, setProjectId] = useState(() => searchParams.get('project') || '');
  const quoteCode   = searchParams.get('code') || '';

  const [title, setTitle]         = useState('');
  const [items, setItems]         = useState([]);
  const [quoteDiscount, setQuoteDiscount] = useState(null);
  const [quoteDiscountLabel, setQuoteDiscountLabel] = useState('Discount');
  const [additionalDiscount, setAdditionalDiscount] = useState(0);
  const [additionalDiscountLabel, setAdditionalDiscountLabel] = useState('Additional Discount');
  const [activeTab, setActiveTab] = useState('items');
  const [selectedTier, setSelectedTier] = useState('signature'); // 'essential' | 'signature' | 'prestige'
  const [showAddItems, setShowAddItems]   = useState(false);
  const [showCreateItem, setShowCreateItem] = useState(false);
  const [itemInsertTarget, setItemInsertTarget] = useState('standard');
  const [saving, setSaving]       = useState(false);
  const [openMenu, setOpenMenu]   = useState(null);
  /** Multi-select line IDs (`_tmpId`) for bulk actions */
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [bulkModal, setBulkModal] = useState(null); // 'space' | 'category' | 'discount' | null
  const [bulkFormValue, setBulkFormValue] = useState('');
  const [bulkDiscountType, setBulkDiscountType] = useState('%');
  const [bulkDiscountVal, setBulkDiscountVal] = useState(0);
  const [colFilterItem, setColFilterItem] = useState('');
  const [colFilterSpace, setColFilterSpace] = useState('');
  const [colFilterMaterial, setColFilterMaterial] = useState('');
  const [addonsEnabled, setAddonsEnabled] = useState(true);
  const [quoteMaterialOptions, setQuoteMaterialOptions] = useState(() => [...MATERIAL_PRESET_OPTS]);
  const [spaceOptions, setSpaceOptions] = useState(() => [...Space_OPTS]);
  const [uomOptions, setUomOptions] = useState(() => [...UOM_PRESET_OPTS]);
  /** Item Master rows for inline item search (same API as Add Items). */
  const [itemMasterPickerItems, setItemMasterPickerItems] = useState([]);
  const [itemMasterPickerLoading, setItemMasterPickerLoading] = useState(true);
  /** Preserved on save for pre-built PDF quotes */
  const [quoteTemplateMeta, setQuoteTemplateMeta] = useState({
    isTemplateQuote: false,
    templatePdfPath: '',
    bhkType: '',
  });
  const fileRef = useRef();
  const remarksRef = useRef();
  const [charges, setCharges] = useState([]);
  const [quoteFiles, setQuoteFiles] = useState([]);
  const [deletingQuoteFile, setDeletingQuoteFile] = useState('');

  useEffect(() => {
    if (quoteId) {
      getQuoteApi(quoteId).then(r => {
        const q = r.data.data.quote;
        setTitle(q.title || '');
        if (q.client) {
          const cid = typeof q.client === 'object' && q.client !== null ? q.client._id : q.client;
          if (cid) setClientId(cid);
        }
        if (q.project) {
          const pid = typeof q.project === 'object' && q.project !== null ? q.project._id : q.project;
          if (pid) setProjectId(pid);
        }
        const savedTier = q.selectedTier || q.packageTier || q.items?.[0]?._selectedTier;
        // Standardize to signature if no tier was previously assigned
        const initialTier = savedTier || 'signature';
        setSelectedTier(initialTier);
        const mult = getTierMultiplier(initialTier);
        const loadedItems = (q.items || []).map((it) => {
          const direct = it.uom != null && String(it.uom).trim() !== '' ? String(it.uom).trim() : '';
          const pop =
            typeof it.item === 'object' && it.item != null && it.item.uom != null
              ? String(it.item.uom).trim()
              : '';
          const rawUom = direct || pop || 'sqft';
          const currentRate = Number(it.rate) || 0;
          const stdRate =
            it.standardRate != null && !isNaN(Number(it.standardRate)) && Number(it.standardRate) > 0
              ? Number(it.standardRate)
              : (mult ? Math.round(currentRate / mult) : currentRate);
          return normalizeQuoteItem({
            ...it,
            standardRate: stdRate,
            rate: currentRate,
            space: it.space || it.section || '',
            discountType: normalizeDiscountType(it.discountType),
            discount:
              it.discount !== undefined && it.discount !== null
                ? Number(it.discount) || 0
                : defaultDiscountForSection(it.space || it.section || ''),
            remarks: it.remarks || '',
            uom: rawUom,
            _tmpId: Math.random().toString(36).slice(2),
          });
        });
        const addonRows = loadedItems.filter(isQuoteAddonLine);
        const nextAddonsEnabled = addonRows.length ? addonRows.some(isQuoteLineEnabled) : true;
        setAddonsEnabled(nextAddonsEnabled);
        const mappedItems = loadedItems.map((it) => markAddonState(it, isQuoteAddonLine(it) ? nextAddonsEnabled : true));
        setItems(mappedItems);

        const loadedLineDiscount = calcTotals(activeQuoteItems(mappedItems)).discountTotal;
        const savedDiscount = q.discount !== undefined && q.discount !== null ? Number(q.discount) : null;
        if (savedDiscount !== null && !isNaN(savedDiscount) && savedDiscount > loadedLineDiscount + 0.5) {
          setQuoteDiscount(savedDiscount);
        } else {
          setQuoteDiscount(null);
        }
        setQuoteDiscountLabel(String(q.discountLabel || '').trim() || 'Discount');
        setAdditionalDiscount(Math.max(0, Number(q.additionalDiscount) || 0));
        setAdditionalDiscountLabel(String(q.additionalDiscountLabel || 'Additional Discount').trim() || 'Additional Discount');
        setCharges(Array.isArray(q.charges) ? q.charges.map((c) => ({ ...c })) : []);
        setQuoteFiles(Array.isArray(q.files) ? [...q.files] : []);
        setQuoteTemplateMeta({
          isTemplateQuote: !!q.isTemplateQuote,
          templatePdfPath: q.templatePdfPath || '',
          bhkType: q.bhkType || '',
        });
        requestAnimationFrame(() => {
          if (remarksRef.current) remarksRef.current.innerHTML = q.remarks || '';
        });
      }).catch(console.error);
    } else {
      setQuoteDiscount(null);
      setQuoteDiscountLabel('Discount');
      setAdditionalDiscount(0);
      setAdditionalDiscountLabel('Additional Discount');
      setQuoteTemplateMeta({ isTemplateQuote: false, templatePdfPath: '', bhkType: '' });
      setAddonsEnabled(true);
      setCharges([]);
      setQuoteFiles([]);
    }
  }, [quoteId]);

  const loadQuoteMaterialOptions = useCallback(async () => {
    try {
      const r = await api.get("/material-options");
      const raw = Array.isArray(r.data) ? r.data : [];
      const fromDb = raw
        .map((x) => (typeof x === "string" ? x : x?.name))
        .filter(Boolean);
      const extra = fromDb.filter(
        (n) => !MATERIAL_PRESET_OPTS.some((s) => s.toLowerCase() === String(n).toLowerCase())
      );
      extra.sort((a, b) => a.localeCompare(b));
      setQuoteMaterialOptions([...MATERIAL_PRESET_OPTS, ...extra]);
    } catch {
      setQuoteMaterialOptions([...MATERIAL_PRESET_OPTS]);
    }
  }, []);

  const loadSpaceOptions = useCallback(async () => {
    try {
      const r = await api.get("/space-options");
      const raw = Array.isArray(r.data) ? r.data : [];
      const fromDb = raw
        .map((x) => (typeof x === "string" ? x : x?.name))
        .filter(Boolean);
      const extra = fromDb.filter(
        (n) =>
          !/^master\s*bedroom\s*2$/i.test(String(n).trim()) &&
          !Space_OPTS.some(
            (s) => s.toLowerCase().replace(/['’]/g, "") === String(n).toLowerCase().replace(/['’]/g, "")
          )
      );
      extra.sort((a, b) => a.localeCompare(b));
      setSpaceOptions([...Space_OPTS, ...extra]);
    } catch {
      setSpaceOptions([...Space_OPTS]);
    }
  }, []);

  const loadUomOptions = useCallback(async () => {
    try {
      const r = await api.get("/uom-options");
      const raw = Array.isArray(r.data) ? r.data : [];
      const fromDb = raw
        .map((x) => (typeof x === "string" ? x : x?.name))
        .filter(Boolean);
      const extra = fromDb.filter(
        (n) => !UOM_PRESET_OPTS.some((s) => s.toLowerCase() === String(n).toLowerCase())
      );
      extra.sort((a, b) => a.localeCompare(b));
      setUomOptions([...UOM_PRESET_OPTS, ...extra]);
    } catch {
      setUomOptions([...UOM_PRESET_OPTS]);
    }
  }, []);

  useEffect(() => {
    loadQuoteMaterialOptions();
  }, [loadQuoteMaterialOptions]);

  useEffect(() => {
    loadSpaceOptions();
  }, [loadSpaceOptions]);

  useEffect(() => {
    loadUomOptions();
  }, [loadUomOptions]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setItemMasterPickerLoading(true);
      try {
        const all = await fetchItemMasterCatalog(api, { excludeTemplatePresets: true });
        if (!cancelled) setItemMasterPickerItems(all);
      } catch (e) {
        console.error(e);
        if (!cancelled) setItemMasterPickerItems([]);
      } finally {
        if (!cancelled) setItemMasterPickerLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const itemMasterNameOptions = useMemo(
    () => itemMasterPickerItems.map((mi) => mi.name).filter(Boolean),
    [itemMasterPickerItems]
  );
  const titleUsesPresetOption = QUOTE_TITLE_OPTIONS.includes(title);

  const addQuoteMaterialOption = useCallback(
    async (name) => {
      try {
        await api.post("/material-options", { name });
      } catch {
        /* duplicate / validation */
      }
      await loadQuoteMaterialOptions();
    },
    [loadQuoteMaterialOptions]
  );

  const addSpaceToMaster = useCallback(
    async (name) => {
      try {
        await api.post("/space-options", { name });
      } catch {
        // duplicate name / validation — reload list so existing entry is available
      }
      await loadSpaceOptions();
    },
    [loadSpaceOptions]
  );

  const addUomOption = useCallback(
    async (name) => {
      try {
        await api.post("/uom-options", { name });
      } catch {
        /* duplicate / validation */
      }
      await loadUomOptions();
    },
    [loadUomOptions]
  );

  // Close menus on outside click
  useEffect(() => {
    const close = () => {
      setOpenMenu(null);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const handleItemColumnChange = useCallback(
    (tmpId, name) => {
      setItems((prev) =>
        prev.map((it) => {
          if (it._tmpId !== tmpId) return it;
          const trimmed = String(name || '').trim();
          const mi = itemMasterPickerItems.find(
            (m) => String(m.name || '').trim().toLowerCase() === trimmed.toLowerCase()
          );
          if (!mi) {
            const updated = { ...it, itemName: name, item: undefined };
            updated.amount = calcAmount(updated);
            return updated;
          }
          const L = Number(mi.length) || 0;
          const H = Number(mi.height) || 0;
          const len = L > 0 ? L : 0;
          const ht = H > 0 ? H : 0;
          const qty = (len > 0 && ht > 0) ? (len * ht) : (Number(it.qty) > 0 ? Number(it.qty) : 1);
          const rawDs = (mi.defaultSpace && String(mi.defaultSpace).trim()) ? mi.defaultSpace : '';
          const ds = rawDs;
          const cm = isElectricalSection(ds)
            ? 'Approx. Points'
            : (mi.quoteMaterial && String(mi.quoteMaterial).trim()) ? mi.quoteMaterial : '';
          const updated = {
            ...it,
            item: mi._id,
            itemName: mi.name,
            itemDetails:
              cleanMasterDescription(mi.description, mi.defaultSpace) || mi.name,
            space: ds || it.space || it.section || '',
            section: ds || it.section || it.space || '',
            category: cm || it.category || '',
            length: len,
            height: ht,
            qty,
            uom: resolveQuoteLineUom({ uom: mi.uom, item: null }, uomOptions),
            standardRate: Number(mi.rates?.[0]?.clientRate ?? mi.rate ?? 0),
            rate: calcTierRate(Number(mi.rates?.[0]?.clientRate ?? mi.rate ?? 0), selectedTier),
            internalFinish: mi.internalFinish || '',
            externalFinish: mi.externalFinish || '',
            image: mi.image != null ? mi.image : it.image,
            discountType: '%',
            discount: defaultDiscountForSection(ds || it.space || it.section || ''),
          };
          updated.amount = calcAmount(updated);
          return markAddonState(updated, addonsEnabled);
        })
      );
    },
    [addonsEnabled, itemMasterPickerItems, uomOptions]
  );

  const handleTierChange = useCallback(
    (newTier) => {
      setSelectedTier(newTier);
      setItems((prev) =>
        prev.map((it) => {
          if (isQuoteAddonLine(it)) return it;
          const currentMult = getTierMultiplier(selectedTier);
          const baseRate =
            it.standardRate != null && !isNaN(Number(it.standardRate)) && Number(it.standardRate) > 0
              ? Number(it.standardRate)
              : currentMult
                ? Math.round((Number(it.rate) || 0) / currentMult)
                : Number(it.rate) || 0;
          const newRate = calcTierRate(baseRate, newTier);
          const updated = {
            ...it,
            standardRate: baseRate,
            rate: newRate,
          };
          updated.amount = calcAmount(updated);
          return markAddonState(updated, addonsEnabled);
        })
      );
    },
    [selectedTier, addonsEnabled]
  );

  const openAddItemsFor = (target = 'standard') => {
    setItemInsertTarget(target);
    setShowAddItems(true);
  };

  const openCreateItemFor = (target = 'standard') => {
    setItemInsertTarget(target);
    setShowCreateItem(true);
  };

  const applyInsertTarget = (row, target = itemInsertTarget) => {
    if (target !== 'addons') return row;
    return {
      ...row,
      space: 'Electrical Work',
      section: 'Electrical Work',
      category: 'Approx. Points',
      discountType: '%',
      discount: 0,
    };
  };

  const makeEmptyRowForTarget = (target = itemInsertTarget) => {
    const row = applyInsertTarget(emptyItem(), target);
    return target === 'addons'
      ? markAddonState({ ...row, amount: calcAmount(row) }, addonsEnabled)
      : row;
  };

  const addEmptyItemFor = (target = 'standard') => {
    setItems((prev) => [...prev, makeEmptyRowForTarget(target)]);
  };

  const handleAddFromMaster = (masterItems) => {
    const newItems = masterItems.map((mi) => {
      const L = Number(mi.length) || 0;
      const H = Number(mi.height) || 0;
      const len = L > 0 ? L : 0;
      const ht = H > 0 ? H : 0;
      const qty = (len > 0 && ht > 0) ? (len * ht) : 1;
      const rawSec = mi.defaultSpace || '';
      const section = rawSec;
      const row = {
        ...emptyItem(),
        item: mi._id,
        itemName: mi.name,
        itemDetails:
          cleanMasterDescription(mi.description, mi.defaultSpace) || mi.name,
        space: section,
        section,
        category: isElectricalSection(section) ? 'Approx. Points' : mi.quoteMaterial || '',
        length: len,
        height: ht,
        qty,
        uom: resolveQuoteLineUom({ uom: mi.uom, item: null }, uomOptions),
        standardRate: Number(mi.rates?.[0]?.clientRate ?? mi.rate ?? 0),
        rate: calcTierRate(Number(mi.rates?.[0]?.clientRate ?? mi.rate ?? 0), selectedTier),
        discountType: '%',
        discount: defaultDiscountForSection(section),
        internalFinish: mi.internalFinish || '',
        externalFinish: mi.externalFinish || '',
        image: mi.image || null,
      };
      const targetedRow = applyInsertTarget(row);
      return markAddonState({ ...targetedRow, amount: calcAmount(targetedRow) }, addonsEnabled);
    });
    setItems((prev) => [...prev, ...newItems]);
  };

  const handleCreatedItem = (masterItem) => {
    if (masterItem?._id) {
      setItemMasterPickerItems((prev) =>
        prev.some((x) => x._id === masterItem._id) ? prev : [...prev, masterItem]
      );
    }
    const L = Number(masterItem.length) || 0;
    const H = Number(masterItem.height) || 0;
    const len = L > 0 ? L : 0;
    const ht = H > 0 ? H : 0;
    const qty = (len > 0 && ht > 0) ? (len * ht) : 1;
    const rawSec = masterItem.defaultSpace || '';
    const section = rawSec;
    const row = {
      ...emptyItem(),
      item: masterItem._id,
      itemName: masterItem.name,
      itemDetails:
        cleanMasterDescription(masterItem.description, masterItem.defaultSpace) ||
        masterItem.name,
      space: section,
      section,
      category: isElectricalSection(section) ? 'Approx. Points' : masterItem.quoteMaterial || '',
      length: len,
      height: ht,
      qty,
      uom: resolveQuoteLineUom({ uom: masterItem.uom, item: null }, uomOptions),
      standardRate: Number(masterItem.rates?.[0]?.clientRate ?? masterItem.rate ?? 0),
      rate: calcTierRate(Number(masterItem.rates?.[0]?.clientRate ?? masterItem.rate ?? 0), selectedTier),
      discountType: '%',
      discount: defaultDiscountForSection(section),
      internalFinish: masterItem.internalFinish || '',
      externalFinish: masterItem.externalFinish || '',
    };
    const targetedRow = applyInsertTarget(row);
    setItems((prev) => [...prev, markAddonState({ ...targetedRow, amount: calcAmount(targetedRow) }, addonsEnabled)]);
  };

  const updateItem = (tmpId, field, value) => {
    setItems(prev => prev.map(it => {
      if (it._tmpId !== tmpId) return it;
      const updated = { ...it, [field]: value };
      if (field === 'rate') {
        const mult = getTierMultiplier(selectedTier);
        const numVal = Number(value) || 0;
        updated.standardRate = mult ? Math.round(numVal / mult) : numVal;
      }
      if (field === 'space') {
        const previousDefault = defaultDiscountForSection(it.space || it.section || '');
        const shouldApplySpaceDefault =
          normalizeDiscountType(it.discountType) === '%' &&
          Number(it.discount || 0) === previousDefault;
        updated.section = value;
        if (isElectricalSection(value)) updated.category = 'Approx. Points';
        if (!isElectricalSection(value)) {
          delete updated._isAddon;
          delete updated._addonGroup;
          delete updated._addonEnabled;
        }
        updated.discountType = normalizeDiscountType(updated.discountType);
        if (shouldApplySpaceDefault) updated.discount = defaultDiscountForSection(value);
      }
      let qty = Number(it.qty) || 0;
      if (field === 'qty') {
        qty = Number(value) || 0;
      } else if (field === 'length' || field === 'height') {
        const len = field === 'length' ? Number(value) || 0 : Number(it.length) || 0;
        const ht = field === 'height' ? Number(value) || 0 : Number(it.height) || 0;
        if (len > 0 && ht > 0) {
          qty = Math.round(len * ht * 100) / 100;
        } else if (len === 0 && ht === 0) {
          qty = Number(it.qty) > 0 ? Number(it.qty) : 1;
        }
      } else {
        const len = Number(it.length) || 0;
        const ht = Number(it.height) || 0;
        if (len > 0 && ht > 0) {
          qty = Math.round(len * ht * 100) / 100;
        } else {
          qty = Number(it.qty) > 0 ? Number(it.qty) : 1;
        }
      }
      updated.qty = qty;
      updated.amount = calcAmount(updated);
      return markAddonState(updated, addonsEnabled);
    }));
  };

  const selectedIdSet = useMemo(() => new Set(selectedLineIds), [selectedLineIds]);
  const activeItems = useMemo(() => activeQuoteItems(items), [items]);
  const standardItems = useMemo(() => items.filter((it) => !isQuoteAddonLine(it)), [items]);
  const addonItems = useMemo(() => items.filter(isQuoteAddonLine), [items]);

  const handleAddonToggle = (enabled) => {
    setAddonsEnabled(enabled);
    setItems((prev) => prev.map((it) => markAddonState(it, enabled)));
    setSelectedLineIds((prev) =>
      enabled
        ? prev
        : prev.filter((id) => {
            const row = items.find((it) => it._tmpId === id);
            return row && !isQuoteAddonLine(row);
          })
    );
  };

  useEffect(() => {
    setSelectedLineIds((prev) => prev.filter((id) => items.some((it) => it._tmpId === id)));
  }, [items]);

  const toggleLineSelected = (tmpId, checked) => {
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(tmpId);
      else next.delete(tmpId);
      return [...next];
    });
  };

  const bulkDeselectAll = () => setSelectedLineIds([]);

  const bulkRemoveSelected = () => {
    const n = selectedLineIds.length;
    if (n === 0) return;
    if (!window.confirm(`Remove ${n} selected line(s)?`)) return;
    setItems((prev) => prev.filter((it) => !selectedIdSet.has(it._tmpId)));
    setSelectedLineIds([]);
  };

  const openBulkSpaceModal = () => {
    const first = items.find((it) => selectedIdSet.has(it._tmpId));
    setBulkFormValue((first?.space || first?.section || '').trim());
    setBulkModal('space');
  };

  const openBulkCategoryModal = () => {
    const first = items.find((it) => selectedIdSet.has(it._tmpId));
    setBulkFormValue((first?.category || '').trim());
    setBulkModal('category');
  };

  const openBulkDiscountModal = () => {
    const first = items.find((it) => selectedIdSet.has(it._tmpId));
    setBulkDiscountType(normalizeDiscountType(first?.discountType));
    setBulkDiscountVal(Number(first?.discount) || 0);
    setBulkModal('discount');
  };

  const applyBulkSpace = () => {
    const v = bulkFormValue.trim();
    setItems((prev) =>
      prev.map((it) =>
        selectedIdSet.has(it._tmpId) ? { ...it, space: v, section: v } : it
      )
    );
    setBulkModal(null);
  };

  const applyBulkCategory = () => {
    const v = bulkFormValue.trim();
    setItems((prev) =>
      prev.map((it) => (selectedIdSet.has(it._tmpId) ? { ...it, category: v } : it))
    );
    setBulkModal(null);
  };

  const applyBulkDiscount = () => {
    const dt = bulkDiscountType;
    const d = Number(bulkDiscountVal) || 0;
    setItems((prev) =>
      prev.map((it) => {
        if (!selectedIdSet.has(it._tmpId)) return it;
        const updated = { ...it, discountType: dt, discount: d };
        updated.amount = calcAmount(updated);
        return updated;
      })
    );
    setBulkModal(null);
  };

  const removeItem   = (tmpId) => setItems(prev => prev.filter(it => it._tmpId !== tmpId));
  const duplicateItem = (tmpId) => {
    const idx = items.findIndex(it => it._tmpId === tmpId);
    const dup = { ...items[idx], _tmpId: Math.random().toString(36).slice(2) };
    const next = [...items];
    next.splice(idx+1, 0, dup);
    setItems(next);
  };
  const insertEmptyItemAfter = (tmpId) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it._tmpId === tmpId);
      if (idx < 0) return prev;
      const source = prev[idx];
      const row = isQuoteAddonLine(source)
        ? markAddonState({
            ...emptyItem(),
            space: source.space || source.section || 'Electrical Work',
            section: source.section || source.space || 'Electrical Work',
            category: source.category || 'Approx. Points',
            discount: defaultDiscountForSection(source.space || source.section || 'Electrical Work'),
          }, addonsEnabled)
        : emptyItem();
      const next = [...prev];
      next.splice(idx + 1, 0, row);
      return next;
    });
  };
  const moveItem = (tmpId, dir) => {
    const idx = items.findIndex(it => it._tmpId === tmpId);
    if (dir === 'up' && idx > 0) {
      const next = [...items];
      [next[idx-1], next[idx]] = [next[idx], next[idx-1]];
      setItems(next);
    }
    if (dir === 'down' && idx < items.length-1) {
      const next = [...items];
      [next[idx], next[idx+1]] = [next[idx+1], next[idx]];
      setItems(next);
    }
  };

  const reorderItemBeforeTarget = useCallback((sourceTmpId, targetTmpId) => {
    if (!sourceTmpId || sourceTmpId === targetTmpId) return;
    setItems((prev) => {
      const srcIdx = prev.findIndex((x) => x._tmpId === sourceTmpId);
      const dstIdx = prev.findIndex((x) => x._tmpId === targetTmpId);
      if (srcIdx < 0 || dstIdx < 0) return prev;
      const next = [...prev];
      const [row] = next.splice(srcIdx, 1);
      next.splice(dstIdx, 0, row);
      return next;
    });
  }, []);

  const goBack = () => {
    const dest = location.state?.from;
    if (dest && typeof dest === 'string') navigate(dest);
    else navigate(-1);
  };

  const { baseSubTotal, discountTotal } = calcTotals(activeItems);
  const effectiveDiscount = quoteDiscount !== null && quoteDiscount > discountTotal
    ? Math.max(0, Number(quoteDiscount) || 0)
    : Math.max(0, discountTotal);
  const totalBeforeCharges = Math.max(0, baseSubTotal - effectiveDiscount - additionalDiscount);
  const chargeAdj = quoteChargesAdjustment(charges, totalBeforeCharges);
  const grandTotal = Math.max(0, Math.round((totalBeforeCharges + chargeAdj) * 100) / 100);

  const handleSave = async () => {
    if (!clientId && !quoteId) {
      alert("Missing client for this quote. Please create quotes from a Client or Project page.");
      return;
    }
    setSaving(true);
    try {
      const quoteItemsForSave = items
        .map((it, idx) => (idx === 0 ? { ...it, _selectedTier: selectedTier } : it))
        .map(({ _tmpId, space, ...rest }) => ({
          ...rest,
          gst: 0,
          space: space || rest.section || '',
          section: rest.section || space || '',
          remarks: rest.remarks || '',
        }))
        .filter(isMeaningfulQuoteItem)
        .map(cleanQuoteItemForSave);
      const payload = {
        title, client: clientId || undefined, project: projectId || undefined,
        selectedTier,
        packageTier: selectedTier,
        items: quoteItemsForSave,
        charges,
        discount: effectiveDiscount,
        discountLabel: quoteDiscountLabel || 'Discount',
        additionalDiscount,
        additionalDiscountLabel,
        files: quoteFiles,
        remarks: remarksRef.current?.innerHTML || '',
        ...(quoteId && quoteTemplateMeta.isTemplateQuote
          ? {
              isTemplateQuote: true,
              templatePdfPath: quoteTemplateMeta.templatePdfPath || undefined,
              bhkType: quoteTemplateMeta.bhkType || undefined,
            }
          : {}),
      };
      if (quoteId) { await updateQuoteApi(quoteId, payload); }
      else { await createQuoteApi(payload); }
      goBack();
    } catch(e) {
      alert(e.response?.data?.error || e.response?.data?.message || e.message || 'Save failed');
    }
    finally { setSaving(false); }
  };

  const handleQuoteFileInput = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (!quoteId) {
      alert('Save the quote once, then open it again from the list to attach files.');
      return;
    }
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append('file', files[i]);
        await api.post(`/quotes/${quoteId}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      const r = await getQuoteApi(quoteId);
      setQuoteFiles(r.data?.data?.quote?.files || []);
    } catch (err) {
      alert(err.response?.data?.message || 'Upload failed');
    }
  };

  const handleDeleteQuoteFile = async (filePath) => {
    if (!quoteId) return;
    const fileName = String(filePath).split('/').pop();
    if (!fileName) return;
    if (!window.confirm(`Remove attached file "${fileName}"?`)) return;
    setDeletingQuoteFile(filePath);
    try {
      const res = await deleteQuoteFileApi(quoteId, fileName);
      setQuoteFiles(res.data?.data?.files || []);
    } catch (err) {
      alert(err.response?.data?.message || 'Could not delete file');
    } finally {
      setDeletingQuoteFile('');
    }
  };

  const filterQuoteLines = useCallback((sourceItems) => {
    const f = { item: colFilterItem, space: colFilterSpace, material: colFilterMaterial };
    if (!normalizeSearch(f.item) && !normalizeSearch(f.space) && !normalizeSearch(f.material)) return sourceItems;
    return sourceItems.filter((it) => matchesItemSpaceMaterialLine(it, f));
  }, [colFilterItem, colFilterSpace, colFilterMaterial]);

  const standardFilteredItems = useMemo(
    () => filterQuoteLines(standardItems),
    [filterQuoteLines, standardItems]
  );
  const addonFilteredItems = useMemo(
    () => filterQuoteLines(addonItems),
    [addonItems, filterQuoteLines]
  );
  const filteredItems = useMemo(
    () => [...standardFilteredItems, ...addonFilteredItems],
    [addonFilteredItems, standardFilteredItems]
  );
  const quoteLineTables = useMemo(() => [
    {
      key: 'standard',
      title: 'Standard Items',
      allRows: standardItems,
      rows: standardFilteredItems,
      emptyText: 'Please add standard items',
    },
    ...(addonsEnabled ? [{
      key: 'addons',
      title: 'Add-on Items',
      allRows: addonItems,
      rows: addonFilteredItems,
      emptyText: 'No add-on items',
    }] : []),
  ], [addonFilteredItems, addonItems, addonsEnabled, standardFilteredItems, standardItems]);

  const bulkSelectAllFiltered = useCallback(() => {
    setSelectedLineIds(filteredItems.map((it) => it._tmpId));
  }, [filteredItems]);

  const thS = { padding:'11px 12px',fontSize:11,fontWeight:700,color:'#fff',borderBottom:'1px solid #2e1a46',borderRight:'1px solid rgba(255,255,255,0.14)',whiteSpace:'nowrap',background:'#2e1a46',textAlign:'left',position:'sticky',top:0,zIndex:1,verticalAlign:'middle' };
  const thFilterS = { ...thS, paddingTop:6, paddingBottom:8, color:'#6B7280', background:'#FAFAFA', borderBottom:'1px solid #E5E7EB', borderRight:'1px solid #EEF2F7', position:'static' };
  const tdS = { padding:'10px 12px',borderBottom:'1px solid #E5E7EB',borderRight:'1px solid #F1F5F9',verticalAlign:'middle',fontSize:12,lineHeight:1.35 };
  /** Item body cells omit bottom border; insert-gap row draws the divider + add control. */
  const tdItemBody = { ...tdS, borderBottom: 'none' };
  const inS = { width:'100%',padding:'4px 6px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:12,outline:'none',fontFamily:'inherit',background:'#F9FAFB' };
  const selS = { ...inS, cursor:'pointer' };

  return (
    <div className="quote-editor-shell" style={{ display:'flex',flexDirection:'column',height:'100%',background:'#fff',fontFamily:'inherit' }}>
      {/* ── Header ── */}
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 20px',borderBottom:'1px solid #E5E7EB',background:'#fff',position:'sticky',top:0,zIndex:10 }}>
        <span style={{ fontSize:14,fontWeight:700,color:'#374151' }}>{quoteCode || (quoteId ? 'Edit Quote' : 'New Quote')}</span>
        <div style={{ display:'flex',gap:8 }}>
          <button onClick={goBack} style={{ padding:'7px 16px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:13,cursor:'pointer',color:'#374151' }}>Close</button>
          <button onClick={handleSave} disabled={saving} style={{ display:'flex',alignItems:'center',gap:5,padding:'7px 16px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="quote-editor-body" style={{ flex:1,overflowY:'auto',padding:'16px 20px' }}>
        {/* Title */}
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:12,fontWeight:600,color:'#6B7280',display:'block',marginBottom:4 }}>Title</label>
          <select
            value={titleUsesPresetOption ? title : ''}
            onChange={(e) => setTitle(e.target.value)}
            style={{ width:'100%',maxWidth:400,padding:'8px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit',background:'#F9FAFB' }}
          >
            <option value="" disabled>
              {title ? `Current: ${title}` : 'Select quotation type'}
            </option>
            {QUOTE_TITLE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex',borderBottom:'1px solid #E5E7EB',marginBottom:0 }}>
          {['items','Space_summary'].map(t => (
            <button key={t} onClick={()=>setActiveTab(t)}
              style={{ padding:'8px 16px',fontSize:13,fontWeight:activeTab===t?600:400,color:activeTab===t?'#976793':'#6B7280',background:'transparent',border:'none',borderBottom:activeTab===t?'2px solid #976793':'2px solid transparent',cursor:'pointer',whiteSpace:'nowrap',marginBottom:-1,textTransform:'capitalize' }}>
              {t === 'items' ? 'Items' : 'Space Summary'}
            </button>
          ))}
          
        </div>

        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,margin:'12px 0',padding:'10px 12px',border:'1px solid #E5E7EB',borderRadius:8,background:'#FBFAFC',maxWidth:520 }}>
            <div>
              <div style={{ fontSize:13,fontWeight:700,color:'#374151' }}>Add-ons</div>
              <div style={{ fontSize:12,color:'#6B7280',marginTop:2 }}>Electrical Work {addonsEnabled ? 'included' : 'excluded'}</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={addonsEnabled}
              onClick={() => handleAddonToggle(!addonsEnabled)}
              style={{
                width:58,
                height:30,
                border:'none',
                borderRadius:999,
                padding:3,
                background:addonsEnabled ? '#976793' : '#D1D5DB',
                cursor:'pointer',
                display:'flex',
                justifyContent:addonsEnabled ? 'flex-end' : 'flex-start',
                alignItems:'center',
                flexShrink:0,
              }}
            >
              <span style={{ width:24,height:24,borderRadius:'50%',background:'#fff',boxShadow:'0 1px 4px rgba(0,0,0,0.18)' }} />
            </button>
        </div>

        {activeTab === 'Space_summary' && (
          <SpaceSummary
            items={activeItems}
            discountLabel={quoteDiscountLabel}
            discount={effectiveDiscount}
            additionalDiscount={additionalDiscount}
            additionalDiscountLabel={additionalDiscountLabel}
          />
        )}

        {activeTab === 'items' && (
          <>
            {/* Items Table */}
            {quoteLineTables.map((table) => (
            <section key={table.key} style={{ marginBottom: table.key === 'standard' ? 20 : 12 }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,margin:'14px 0 8px' }}>
                <h3 style={{ margin:0,fontSize:14,fontWeight:700,color:'#1F2937' }}>{table.title}</h3>
                <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:8 }}>
                  {table.key === 'standard' && (
                    <div style={{ display:'inline-flex',alignItems:'center',background:'#F3F4F6',padding:3,borderRadius:8,border:'1px solid #E5E7EB',gap:4 }}>
                      <button
                        type="button"
                        onClick={() => handleTierChange('essential')}
                        style={{
                          padding:'5px 16px',
                          borderRadius:6,
                          fontSize:12,
                          fontWeight:600,
                          cursor:'pointer',
                          border:'none',
                          background:selectedTier === 'essential' ? '#2E1A46' : 'transparent',
                          color:selectedTier === 'essential' ? '#fff' : '#4B5563',
                          boxShadow:selectedTier === 'essential' ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
                          transition:'all 0.15s ease',
                        }}
                      >
                        Essential
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTierChange('signature')}
                        style={{
                          padding:'5px 16px',
                          borderRadius:6,
                          fontSize:12,
                          fontWeight:600,
                          cursor:'pointer',
                          border:'none',
                          background:selectedTier === 'signature' ? '#2E1A46' : 'transparent',
                          color:selectedTier === 'signature' ? '#fff' : '#4B5563',
                          boxShadow:selectedTier === 'signature' ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
                          transition:'all 0.15s ease',
                        }}
                      >
                        Signature
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTierChange('prestige')}
                        style={{
                          padding:'5px 16px',
                          borderRadius:6,
                          fontSize:12,
                          fontWeight:600,
                          cursor:'pointer',
                          border:'none',
                          background:selectedTier === 'prestige' ? '#2E1A46' : 'transparent',
                          color:selectedTier === 'prestige' ? '#fff' : '#4B5563',
                          boxShadow:selectedTier === 'prestige' ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
                          transition:'all 0.15s ease',
                        }}
                      >
                        Prestige
                      </button>
                    </div>
                  )}
                  <div style={{ display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',justifyContent:'flex-end' }}>
                    {table.key === 'addons' ? (
                    <span style={{ fontSize:12,fontWeight:700,color:addonsEnabled ? '#047857' : '#6B7280',background:addonsEnabled ? '#D1FAE5' : '#F3F4F6',borderRadius:999,padding:'4px 10px' }}>
                      {addonsEnabled ? 'Included in total' : 'Not in total'}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => openAddItemsFor(table.key === 'addons' ? 'addons' : 'standard')}
                    style={{ display:'flex',alignItems:'center',gap:6,padding:'6px 12px',background:table.key === 'addons' ? '#2563EB' : '#976793',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer' }}
                  >
                    {table.key === 'addons' ? '+ Add Add-on Items' : '+ Add Items'}
                  </button>
                  <button
                    type="button"
                    onClick={() => addEmptyItemFor(table.key === 'addons' ? 'addons' : 'standard')}
                    style={{ display:'flex',alignItems:'center',gap:6,padding:'6px 12px',background:'#fff',color:'#374151',border:'1px solid #E5E7EB',borderRadius:8,fontSize:12,fontWeight:500,cursor:'pointer' }}
                  >
                    {table.key === 'addons' ? '+ Blank Add-on' : '+ Blank Line'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openCreateItemFor(table.key === 'addons' ? 'addons' : 'standard')}
                    style={{ display:'flex',alignItems:'center',gap:6,padding:'6px 12px',background:'#fff',color:'#374151',border:'1px solid #E5E7EB',borderRadius:8,fontSize:12,fontWeight:500,cursor:'pointer' }}
                  >
                    {table.key === 'addons' ? '+ Create Add-on' : '+ Create Item'}
                  </button>
                  </div>
                </div>
              </div>
            <div className="quote-editor-table-wrap" style={{ overflowX:'auto',border:'1px solid #E5E7EB',borderRadius:8 }}>
              <table style={{ width:'100%',borderCollapse:'collapse',minWidth:1140 }}>
                <thead>
                  <tr>
                    <th style={{ ...thS, textAlign:'center', width: 62 }}>Sr.No.</th>
                    <th style={{ ...thS, width: 44, textAlign:'center' }} aria-label="Select rows" />
                    <th style={thS}>Item</th>
                    <th style={thS}>Space</th>
                    <th style={thS}>Material</th>
                    <th style={thS}>Length</th>
                    <th style={thS}>Height</th>
                    <th style={thS}>Qty</th>
                    <th style={thS}>UOM</th>
                    <th style={thS}>Rate</th>
                    <th style={thS}>Discount</th>
                    <th style={{...thS,textAlign:'right'}}>Amount</th>
                    <th style={{...thS,textAlign:'right'}}>Discounted Amount</th>
                    <th style={{ ...thS, textAlign:'center' }}>Actions</th>
                  </tr>
                  <tr style={{ background:'#FAFAFA' }}>
                    <th style={{ ...thFilterS, textAlign:'center' }} />
                    <th style={{ ...thFilterS, textAlign:'center' }} />
                    <th style={{ ...thFilterS, verticalAlign:'top' }}>
                      <ColumnFilterInput value={colFilterItem} onChange={(e)=>setColFilterItem(e.target.value)} placeholder="Filter item…" aria-label="Filter by item" />
                    </th>
                    <th style={{ ...thFilterS, verticalAlign:'top' }}>
                      <ColumnFilterInput value={colFilterSpace} onChange={(e)=>setColFilterSpace(e.target.value)} placeholder="Filter space…" aria-label="Filter by space" />
                    </th>
                    <th style={{ ...thFilterS, verticalAlign:'top' }}>
                      <ColumnFilterInput value={colFilterMaterial} onChange={(e)=>setColFilterMaterial(e.target.value)} placeholder="Filter material…" aria-label="Filter by material" />
                    </th>
                    <th style={thFilterS} colSpan={9} />
                  </tr>
                </thead>
                <tbody>
                  {table.allRows.length === 0 ? (
                    <tr><td colSpan={14} style={{ textAlign:'center',padding:'3rem',color:'#9CA3AF',fontSize:13 }}>{table.emptyText}</td></tr>
                  ) : table.rows.length === 0 ? (
                    <tr><td colSpan={14} style={{ textAlign:'center',padding:'2rem',color:'#9CA3AF',fontSize:13 }}>No lines match your search</td></tr>
                  ) : table.rows.flatMap((item, idx) => [
                    <tr
                      key={item._tmpId}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const srcId = e.dataTransfer.getData(DRAG_MIME);
                        reorderItemBeforeTarget(srcId, item._tmpId);
                      }}
                    >
                      <td
                        draggable
                        title="Drag to reorder"
                        onDragStart={(e) => {
                          e.dataTransfer.setData(DRAG_MIME, item._tmpId);
                          e.dataTransfer.effectAllowed = "move";
                          try {
                            const img = new Image();
                            img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
                            e.dataTransfer.setDragImage(img, 0, 0);
                          } catch { /* ignore */ }
                        }}
                        style={{
                          ...tdItemBody,
                          color: "#6B7280",
                          fontSize: 11,
                          width: 62,
                          cursor: "grab",
                          userSelect: "none",
                          verticalAlign: "middle",
                          textAlign: "center",
                          fontWeight: 700,
                        }}
                      >
                        <span style={{ marginRight: 6, color: "#9CA3AF", letterSpacing: "-2px" }}>⠿</span>
                        {idx + 1}
                      </td>
                      <td style={{ ...tdItemBody, width: 40, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIdSet.has(item._tmpId)}
                          onChange={(e) => toggleLineSelected(item._tmpId, e.target.checked)}
                          aria-label={`Select row ${idx + 1}`}
                        />
                      </td>
                      <td style={{ ...tdItemBody, minWidth:200 }}>
                        <div style={{ minWidth: 0 }}>
                          <SearchableSelectWithAdd
                            value={item.itemName || ''}
                            onChange={(v) => handleItemColumnChange(item._tmpId, v)}
                            options={itemMasterNameOptions}
                            onAddNew={async () => {
                              /* custom line label only; not saved to Item Master */
                            }}
                            addPrompt="as custom line"
                            placeholder={
                              itemMasterPickerLoading
                                ? 'Loading Item Master…'
                                : 'Search Item Master…'
                            }
                            style={{ width: '100%', marginBottom: 4 }}
                          />
                          <textarea
                            key={`${item._tmpId}-${item.item ?? ''}-${item.itemName ?? ''}`}
                            defaultValue={item.itemDetails || ''}
                            onBlur={(e) => updateItem(item._tmpId, 'itemDetails', e.target.value)}
                            rows={2}
                            style={{ width:'100%',padding:'3px 6px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:11,resize:'none',outline:'none',fontFamily:'inherit',background:'#F9FAFB',color:'#6B7280' }}
                          />
                        </div>
                      </td>
                      <td style={{ ...tdItemBody, minWidth:140 }}>
                        <SearchableSelectWithAdd
                          value={item.space || item.section || ""}
                          onChange={(v) => updateItem(item._tmpId, "space", v)}
                          options={spaceOptions}
                          onAddNew={addSpaceToMaster}
                          addPrompt="as space"
                          placeholder="Search or add space…"
                        />
                      </td>
                      <td style={{ ...tdItemBody, minWidth:140 }}>
                        <SearchableSelectWithAdd
                          value={item.category || ""}
                          onChange={(v) => updateItem(item._tmpId, "category", v)}
                          options={quoteMaterialOptions}
                          onAddNew={addQuoteMaterialOption}
                          addPrompt="to materials list"
                          placeholder="Search or add material…"
                        />
                      </td>
{/* LENGTH */}
<td style={{ ...tdItemBody, width:70 }}>
  <input
    style={inS}
    type="number"
    value={item.length || ''}
    onChange={e =>
      updateItem(item._tmpId, 'length', parseFloat(e.target.value) || 0)
    }
  />
</td>

{/* HEIGHT */}
<td style={{ ...tdItemBody, width:70 }}>
  <input
    style={inS}
    type="number"
    value={item.height || ''}
    onChange={e =>
      updateItem(item._tmpId, 'height', parseFloat(e.target.value) || 0)
    }
  />
</td>

{/* QTY */}
<td style={{ ...tdItemBody, width:70 }}>
  {(Number(item.length) > 0 && Number(item.height) > 0) ? (
    <span title="Calculated from Length × Height" style={{ color: '#374151', fontSize: 12, fontWeight: 500 }}>
      {(Number(item.length) * Number(item.height)).toFixed(2)}
    </span>
  ) : (
    <input
      style={inS}
      type="number"
      min="0"
      step="any"
      value={item.qty !== undefined && item.qty !== null ? item.qty : 1}
      onChange={(e) => updateItem(item._tmpId, 'qty', parseFloat(e.target.value) || 0)}
      title="Quantity (pcs / nos / units)"
    />
  )}
</td>                    
<td style={{ ...tdItemBody, minWidth: 96, maxWidth: 120 }} onMouseDown={(e) => e.stopPropagation()}>
  <SearchableSelectWithAdd
    value={resolveQuoteLineUom(item, uomOptions)}
    onChange={(v) =>
      updateItem(
        item._tmpId,
        'uom',
        resolveQuoteLineUom({ uom: (v && String(v).trim()) || 'sqft', item: null }, uomOptions)
      )
    }
    options={uomOptions}
    onAddNew={addUomOption}
    addPrompt="to UOM list"
    placeholder="Search or add UOM…"
    style={{ width: '100%', minWidth: 0 }}
  />
</td>
                      
                    <td style={{ ...tdItemBody, width:90 }}>
  <div style={{ display:'flex',alignItems:'center',gap:2 }}>
    <span style={{ color:'#9CA3AF',fontSize:11 }}>₹</span>
    <input
      style={{...inS,width:70}}
      type="number"
      value={item.rate}
      onChange={e=>updateItem(item._tmpId,'rate',parseFloat(e.target.value)||0)}
    />
  </div>
</td>


                      <td style={{ ...tdItemBody, width: 118 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <button
                            type="button"
                            title={normalizeDiscountType(item.discountType) === CURRENCY_DISCOUNT_TYPE ? 'Flat amount (₹). Tap to use percent.' : 'Percent (%). Tap to use flat ₹.'}
                            aria-label={normalizeDiscountType(item.discountType) === CURRENCY_DISCOUNT_TYPE ? 'Switch discount to percent' : 'Switch discount to rupees'}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const next = normalizeDiscountType(item.discountType) === CURRENCY_DISCOUNT_TYPE ? '%' : CURRENCY_DISCOUNT_TYPE;
                              updateItem(item._tmpId, 'discountType', next);
                            }}
                            style={{
                              flexShrink: 0,
                              width: 38,
                              height: 32,
                              padding: 0,
                              border: '1px solid #D1D5DB',
                              borderRadius: 8,
                              background: normalizeDiscountType(item.discountType) === CURRENCY_DISCOUNT_TYPE ? '#ECFDF5' : '#F7F1F7',
                              color: normalizeDiscountType(item.discountType) === CURRENCY_DISCOUNT_TYPE ? '#047857' : '#2e1a46',
                              fontSize: 15,
                              fontWeight: 700,
                              cursor: 'pointer',
                              lineHeight: 1,
                              fontFamily: 'inherit',
                              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)',
                            }}
                          >
                            {normalizeDiscountType(item.discountType) === CURRENCY_DISCOUNT_TYPE ? '₹' : '%'}
                          </button>
                          <input
                            style={{ ...inS, width: 58, minWidth: 0 }}
                            type="number"
                            value={item.discount || 0}
                            onChange={(e) => updateItem(item._tmpId, 'discount', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </td>

                      <td style={{...tdItemBody,textAlign:'right',fontWeight:500,color:'#374151',whiteSpace:'nowrap',width:90}}>
                        ₹{formatInr(quoteLineBase(item))}
                      </td>

                      <td style={{...tdItemBody,textAlign:'right',fontWeight:600,color:'#1F2937',whiteSpace:'nowrap',width:115}}>
                        <div>₹{formatInr(calcAmount(item))}</div>
                        {quoteLineDiscountAmount(item) > 0 && (
                          <div style={{ fontSize: 10, color: '#059669', fontWeight: 500, marginTop: 1 }}>
                            (-₹{formatInr(quoteLineDiscountAmount(item))})
                          </div>
                        )}
                      </td>
 <td style={{ ...tdItemBody, width:40, position:'relative' }}>
                        <button onClick={e=>{e.stopPropagation();setOpenMenu(m=>m===item._tmpId?null:item._tmpId);}}
                          style={{ background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',padding:4 }}>⋮</button>
                        {openMenu === item._tmpId && (
                          <div style={{ position:'absolute',right:0,top:32,background:'#fff',border:'1px solid #E5E7EB',borderRadius:8,boxShadow:'0 4px 20px rgba(0,0,0,0.1)',zIndex:100,minWidth:150,overflow:'hidden' }}
                            onClick={e=>e.stopPropagation()}>
                            {[['Remove','#EF4444'],['Create Duplicate','#374151'],['Move Up','#374151'],['Move Down','#374151']].map(([label,color])=>(
                              <button key={label} onClick={()=>{
                                if(label==='Remove') removeItem(item._tmpId);
                                if(label==='Create Duplicate') duplicateItem(item._tmpId);
                                if(label==='Move Up') moveItem(item._tmpId,'up');
                                if(label==='Move Down') moveItem(item._tmpId,'down');
                                setOpenMenu(null);
                              }}
                                style={{ display:'flex',alignItems:'center',gap:8,width:'100%',textAlign:'left',padding:'8px 14px',border:'none',background:'transparent',fontSize:12,cursor:'pointer',color }}
                                onMouseEnter={e=>e.currentTarget.style.background='#F9FAFB'}
                                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                {label==='Remove' && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>}
                                {label==='Create Duplicate' && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>}
                                {label==='Move Up'   && '↑'} {label==='Move Down' && '↓'}
                                {label}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>,
                    <tr key={`${item._tmpId}-insert-gap`}>
                      <td
                        colSpan={14}
                        style={{
                          padding: 0,
                          background: '#fff',
                          borderTop: 'none',
                          borderBottom: 'none',
                          verticalAlign: 'middle',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          style={{
                            position: 'relative',
                            height: 26,
                            margin: '4px 0 8px',
                          }}
                        >
                          <div
                            aria-hidden
                            style={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              top: '50%',
                              height: 1,
                              background: '#E5E7EB',
                              pointerEvents: 'none',
                            }}
                          />
                          <button
                            type="button"
                            title="Add blank line below this row"
                            aria-label={`Add blank line after row ${idx + 1}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              insertEmptyItemAfter(item._tmpId);
                            }}
                            onMouseEnter={(e) => {
                              const b = e.currentTarget;
                              b.style.color = '#fff';
                              b.style.background = '#976793';
                              b.style.boxShadow = '0 0 0 2px rgba(79, 142, 247, 0.35)';
                            }}
                            onMouseLeave={(e) => {
                              const b = e.currentTarget;
                              b.style.color = '#976793';
                              b.style.background = '#fff';
                              b.style.boxShadow = '0 0 0 1px #E5E7EB';
                            }}
                            style={{
                              position: 'absolute',
                              left: 28,
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              border: 'none',
                              background: '#fff',
                              color: '#976793',
                              fontSize: 18,
                              fontWeight: 600,
                              lineHeight: 1,
                              cursor: 'pointer',
                              padding: 0,
                              margin: 0,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontFamily: 'inherit',
                              outline: 'none',
                              boxShadow: '0 0 0 1px #E5E7EB',
                              WebkitTapHighlightColor: 'transparent',
                              zIndex: 1,
                            }}
                          >
                            +
                          </button>
                        </div>
                      </td>
                    </tr>,
                  ])}
                </tbody>
              </table>
            </div>
            </section>
            ))}

            {/* Add Item / Create Item buttons */}
            <div style={{ display:'flex',gap:10,marginTop:12 }}>
              <button onClick={()=>openAddItemsFor('standard')}
                style={{ display:'flex',alignItems:'center',gap:6,padding:'7px 16px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
                + Add Standard Items
              </button>
              <button onClick={()=>openCreateItemFor('standard')}
                style={{ display:'flex',alignItems:'center',gap:6,padding:'7px 16px',background:'#fff',color:'#374151',border:'1px solid #E5E7EB',borderRadius:8,fontSize:13,fontWeight:500,cursor:'pointer' }}>
                + Create Standard Item
              </button>
            </div>

            {/* Totals — gross sub total, aggregate line discounts, then charges and grand total */}
            <div style={{ display:'flex',justifyContent:'flex-end',marginTop:16,marginBottom:16 }}>
              <div style={{ textAlign:'right',fontSize:13 }}>
                <div style={{ color:'#6B7280',marginBottom:3 }}>Sub Total: <strong>₹{formatInr(baseSubTotal)}</strong></div>
                <label style={{ display:'flex',alignItems:'center',justifyContent:'flex-end',gap:8,color:'#6B7280',marginBottom:6 }}>
                  <input
                    type="text"
                    value={quoteDiscountLabel}
                    onChange={(e) => setQuoteDiscountLabel(e.target.value)}
                    onBlur={() => setQuoteDiscountLabel((v) => String(v || '').trim() || 'Discount')}
                    aria-label="Discount label"
                    placeholder="Discount"
                    maxLength={80}
                    style={{ width:165,padding:'4px 6px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:12,textAlign:'right',fontFamily:'inherit',background:'#F9FAFB',fontWeight:700,color:'#374151' }}
                  />
                  <span>₹</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={quoteDiscount !== null && quoteDiscount > discountTotal ? quoteDiscount : (discountTotal > 0 ? discountTotal : '')}
                    placeholder={discountTotal > 0 ? String(discountTotal) : '0'}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || parseFloat(val) <= discountTotal) {
                        setQuoteDiscount(null);
                      } else {
                        setQuoteDiscount(Math.max(0, parseFloat(val) || 0));
                      }
                    }}
                    style={{ width:120,padding:'4px 6px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:12,textAlign:'right',fontFamily:'inherit',background:'#F9FAFB',fontWeight:700,color:'#374151' }}
                  />
                </label>
                <label style={{ display:'flex',alignItems:'center',justifyContent:'flex-end',gap:8,color:'#6B7280',marginBottom:6 }}>
                  <input
                    type="text"
                    value={additionalDiscountLabel}
                    onChange={(e) => setAdditionalDiscountLabel(e.target.value)}
                    placeholder="Additional Discount"
                    style={{ width:165,padding:'4px 6px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:12,textAlign:'right',fontFamily:'inherit',background:'#F9FAFB',fontWeight:700,color:'#374151' }}
                  />
                  <span>₹</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={additionalDiscount}
                    onChange={(e) => setAdditionalDiscount(Math.max(0, parseFloat(e.target.value) || 0))}
                    style={{ width:120,padding:'4px 6px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:12,textAlign:'right',fontFamily:'inherit',background:'#F9FAFB',fontWeight:700,color:'#374151' }}
                  />
                </label>
                {chargeAdj !== 0 ? (
                  <div style={{ color:'#6B7280',marginBottom:3 }}>Charges (net): <strong>₹{formatInr(chargeAdj)}</strong></div>
                ) : null}
                <div style={{ fontWeight:700,fontSize:14,color:'#1F2937' }}>Total: ₹{formatInr(grandTotal)}</div>
              </div>
            </div>

            {/* Select Files */}
            <div style={{ marginBottom:16 }} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize:12,fontWeight:600,color:'#374151',marginBottom:6 }}>Select Files</div>
              {quoteFiles.length === 0 ? (
                <div style={{ fontSize:12,color:'#9CA3AF',marginBottom:6 }}>No files attached</div>
              ) : (
                <ul style={{ margin:'0 0 8px',paddingLeft:0,listStyle:'none',fontSize:12,color:'#374151' }}>
                  {quoteFiles.map((f, i) => (
                    <li key={`${f}-${i}`} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,marginBottom:6,padding:'6px 8px',background:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:8 }}>
                      <a href={f.startsWith('http') ? f : `${BASE_URL}${f}`} target="_blank" rel="noreferrer" style={{ color:'#976793',wordBreak:'break-all' }}>{String(f).split('/').pop()}</a>
                      <button
                        type="button"
                        onClick={() => handleDeleteQuoteFile(f)}
                        disabled={deletingQuoteFile === f}
                        style={{ flexShrink:0,padding:'4px 10px',background:'#fff',color:'#B91C1C',border:'1px solid #FECACA',borderRadius:6,fontSize:11,fontWeight:600,cursor:deletingQuoteFile === f ? 'not-allowed' : 'pointer',opacity:deletingQuoteFile === f ? 0.6 : 1 }}
                      >
                        {deletingQuoteFile === f ? 'Removing...' : 'Delete'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button type="button" onClick={(ev) => { ev.stopPropagation(); fileRef.current?.click(); }} style={{ display:'flex',alignItems:'center',gap:5,padding:'6px 14px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Upload
              </button>
              <input ref={fileRef} type="file" multiple style={{ display:'none' }} onChange={handleQuoteFileInput} />
            </div>

            {/* Terms */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12,fontWeight:600,color:'#374151',marginBottom:6 }}>Terms and conditions</div>
              <select style={{ padding:'7px 12px',border:'1px solid #E5E7EB',borderRadius:8,fontSize:12,outline:'none',background:'#F9FAFB',minWidth:200,fontFamily:'inherit' }}>
                <option value="">Select</option>
                <option>Standard Terms</option>
              </select>
            </div>

            {/* Remarks */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12,fontWeight:600,color:'#374151',marginBottom:6 }}>Remarks</div>
              <div style={{ border:'1px solid #E5E7EB',borderRadius:8,overflow:'hidden' }}>
                {/* Toolbar */}
                <div style={{ display:'flex',gap:2,padding:'5px 8px',borderBottom:'1px solid #E5E7EB',background:'#F9FAFB',flexWrap:'wrap' }}>
                  {['B','I','U','S','P','H1','H2','H3','•','1.','❝','—'].map(cmd=>(
                    <button key={cmd} onMouseDown={e=>{e.preventDefault();document.execCommand(cmd==='B'?'bold':cmd==='I'?'italic':cmd==='U'?'underline':cmd==='S'?'strikeThrough':cmd==='❝'?'blockquote':cmd==='—'?'insertHorizontalRule':['H1','H2','H3','P'].includes(cmd)?'formatBlock':'insertUnorderedList',cmd==='•'?false:false,['H1','H2','H3','P'].includes(cmd)?cmd.toLowerCase():null);}}
                      style={{ padding:'3px 6px',border:'none',background:'transparent',borderRadius:4,fontSize:11,cursor:'pointer',fontWeight:cmd==='B'?700:400 }}
                      onMouseEnter={e=>e.currentTarget.style.background='#E5E7EB'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>{cmd}</button>
                  ))}
                  {/* Voice */}
                  <div style={{ marginLeft:'auto',display:'flex',flexDirection:'column',alignItems:'center' }}>
                    <div style={{ width:28,height:28,borderRadius:'50%',background:'#6366F1',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/></svg>
                    </div>
                    <span style={{ fontSize:9,color:'#9CA3AF' }}>00:00</span>
                  </div>
                </div>
                <div ref={remarksRef} contentEditable suppressContentEditableWarning
                  style={{ minHeight:80,padding:'10px 12px',fontSize:13,color:'#374151',outline:'none',lineHeight:1.6 }} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bulk selection floating bar */}
      {activeTab === 'items' && selectedLineIds.length > 0 && (
        <div
          role="toolbar"
          aria-label="Bulk actions for selected lines"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: 4,
            padding: '10px 14px',
            background: '#15274a',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0,0,0,0.28)',
            maxWidth: '96vw',
            overflowX: 'auto',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <span style={{ background: '#3b82f6', color: '#fff', padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, marginRight: 6, flexShrink: 0 }}>
            {selectedLineIds.length} item{selectedLineIds.length === 1 ? '' : 's'}
          </span>
          {[
            { key: 'sec', label: 'Set Section', icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
            ), onClick: openBulkSpaceModal },
            { key: 'cat', label: 'Set Material', icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
            ), onClick: openBulkCategoryModal },
            { key: 'disc', label: 'Set Discount', icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
            ), onClick: openBulkDiscountModal },
            { key: 'all', label: 'Select All', icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
            ), onClick: bulkSelectAllFiltered },
            { key: 'none', label: 'Deselect All', icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
            ), onClick: bulkDeselectAll },
            { key: 'rm', label: 'Remove', icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FCA5A5" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            ), onClick: bulkRemoveSelected, danger: true },
          ].map((btn) => (
            <button
              key={btn.key}
              type="button"
              onClick={btn.onClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px',
                border: 'none',
                borderRadius: 8,
                background: btn.danger ? 'rgba(239,68,68,0.2)' : 'transparent',
                color: '#fff',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => { if (!btn.danger) e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = btn.danger ? 'rgba(239,68,68,0.2)' : 'transparent'; }}
            >
              {btn.icon}
              {btn.label}
            </button>
          ))}
        </div>
      )}

      {/* Bulk edit modals */}
      {bulkModal === 'space' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={(e) => { if (e.target === e.currentTarget) setBulkModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '20px 22px', minWidth: 320, maxWidth: 440, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#1F2937' }}>Set space (section)</h3>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B7280' }}>Applies to {selectedLineIds.length} selected line(s).</p>
            <SearchableSelectWithAdd
              value={bulkFormValue}
              onChange={setBulkFormValue}
              options={spaceOptions}
              onAddNew={addSpaceToMaster}
              addPrompt="as space"
              placeholder="Search or add space…"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button type="button" onClick={() => setBulkModal(null)} style={{ padding: '8px 16px', border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={applyBulkSpace} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#976793', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Apply</button>
            </div>
          </div>
        </div>
      )}
      {bulkModal === 'category' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={(e) => { if (e.target === e.currentTarget) setBulkModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '20px 22px', minWidth: 320, maxWidth: 440, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#1F2937' }}>Set material</h3>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B7280' }}>Applies to {selectedLineIds.length} selected line(s).</p>
            <SearchableSelectWithAdd
              value={bulkFormValue}
              onChange={setBulkFormValue}
              options={quoteMaterialOptions}
              onAddNew={addQuoteMaterialOption}
              addPrompt="to materials list"
              placeholder="Search or add material…"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button type="button" onClick={() => setBulkModal(null)} style={{ padding: '8px 16px', border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={applyBulkCategory} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#976793', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Apply</button>
            </div>
          </div>
        </div>
      )}
      {bulkModal === 'discount' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={(e) => { if (e.target === e.currentTarget) setBulkModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '20px 22px', minWidth: 320, maxWidth: 400, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#1F2937' }}>Set discount</h3>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B7280' }}>Applies to {selectedLineIds.length} selected line(s).</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <button
                type="button"
                onClick={() => setBulkDiscountType((t) => (t === '₹' ? '%' : '₹'))}
                style={{
                  width: 40,
                  height: 36,
                  border: '1px solid #D1D5DB',
                  borderRadius: 8,
                  background: bulkDiscountType === '₹' ? '#ECFDF5' : '#F7F1F7',
                  color: bulkDiscountType === '₹' ? '#047857' : '#2e1a46',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {bulkDiscountType === '₹' ? '₹' : '%'}
              </button>
              <input
                type="number"
                value={bulkDiscountVal}
                onChange={(e) => setBulkDiscountVal(parseFloat(e.target.value) || 0)}
                style={{ flex: 1, padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14 }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={() => setBulkModal(null)} style={{ padding: '8px 16px', border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={applyBulkDiscount} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#976793', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {showAddItems && <AddItemsModal tier={selectedTier} onClose={()=>setShowAddItems(false)} onAdd={handleAddFromMaster} />}
      {showCreateItem && (
        <CreateItemModal
          onClose={() => setShowCreateItem(false)}
          onCreated={handleCreatedItem}
          materialOptions={quoteMaterialOptions}
          onAddMaterial={addQuoteMaterialOption}
          uomOptions={uomOptions}
          onAddUom={addUomOption}
        />
      )}
    </div>
  );
}
